import { ScheduleApi } from '../apis/schedule/schedule-api';
import { ScheduleRowApi } from '../apis/schedule/schedule-row-api';
import { ScheduleFieldApi } from '../apis/schedule/schedule-field-api';
import { ScheduleFieldEntity } from '../apis/schedule/schedule-field-entity';
import { SerieApi } from '../apis/serie/serie-api';
import { CustomIntervalApi } from '../apis/custom-interval/custom-interval-api';
import { Project } from '../model/project';
import { inject } from 'aurelia';
import { EventApi } from '../apis/event/event-api';
import { GroupsApi } from '../apis/group/groups-api';
import { VenuesApi } from '../apis/venue/venues-api';
import { UserContext } from '../contexts/user-context';
import { EventEntity } from '../apis/event/event-entity';
import { GroupEntity } from '../apis/group/group-entity';
import { VenueEntity } from '../apis/venue/venue-entity';
import { ScheduleEntity } from '../apis/schedule/schedule-entity';
import { SchedulerModel } from '../model/scheduler-model';
import { LanguageResolver } from '../infrastructure/language-resolver';
import { UserApi } from '../apis/user/user-api';
import { TeamApi } from '../apis/team/team-api';
import { TeamSettingEntity } from '../apis/team/team-setting-entity';
import { SerieSettingEntity } from '../apis/serie/serie-setting-entity';
import { CustomIntervalEntity } from '../apis/custom-interval/custom-interval-entity';
import { ScheduleRowEntity } from '../apis/schedule/schedule-row-entity';
import { EventCommentEntity } from '../apis/event/event-comment-entity';
import { GroupAssociationMembersApi } from '../apis/association/group-association-members-api';
import { GroupAssociationsApi } from '../apis/association/group-associations-api';
import { v1 as uuidv1 } from 'uuid';

export interface ProjectDraftData {
    event: any;
    groups: any[];
    venues: any[];
    schedules: any[];
    groupTeams: { groupId: string; teams: any[] }[];
    associationContacts: { key: string; contacts: StagedAssociationContact[] }[];
    scheduleCache: { scheduleId: string; serie: any; customIntervals: any[]; games: any[]; fields: any[] }[];
    fieldAssignments: { scheduleId: string; fields: any[] }[];
}

// A staged association contact: `id` is set once persisted on the backend, undefined for a
// not-yet-saved local invite.
export interface StagedAssociationContact {
    id?: string;
    email: string;
}

@inject(EventApi, GroupsApi, VenuesApi, ScheduleApi, SerieApi, ScheduleRowApi, CustomIntervalApi, UserContext, LanguageResolver, UserApi, TeamApi, ScheduleFieldApi, GroupAssociationMembersApi, GroupAssociationsApi)
export class ProjectModelService {
    private _model: Project | null = null;
    private _eventSnapshot: string | null = null;
    public pendingSchedulerModels: SchedulerModel[] = [];
    public pendingGroupDeletes: string[] = [];
    public pendingVenueDeletes: string[] = [];
    public pendingScheduleDeletes: string[] = [];
    private _originalGroupIds: Set<string> = new Set();
    private _originalVenueIds: Set<string> = new Set();
    private _originalScheduleIds: Set<string> = new Set();
    private _dirtyGroupIds: Set<string> = new Set();
    private _dirtyVenueIds: Set<string> = new Set();
    private _dirtyVenueScheduleIds: Set<string> = new Set();
    private _dirtyTeamGroupIds: Set<string> = new Set();
    private _dirtyAssociationContactKeys: Set<string> = new Set();
    // Schedule ids whose sortOrder changed via reorderSchedule() without otherwise being edited.
    private _dirtyScheduleIds: Set<string> = new Set();
    // Member ids queued for deletion on the next save, keyed like _associationContactsCache.
    private _pendingAssociationMemberRemovals: Map<string, Set<string>> = new Map();
    // Associations fully removed (via the edit-teams trash icon) and queued for backend deletion
    // on the next save, keyed by their server associationId with the owning groupId as the value.
    private _pendingAssociationDeletes: Map<string, string> = new Map();
    // Associations renamed in edit-teams and queued for a backend rename on the next save, keyed by
    // server associationId. `from` is the name the backend currently has, `to` the wanted name.
    private _pendingAssociationRenames: Map<string, { groupId: string; from: string; to: string }> = new Map();
    // Schedule rows whose team names were rewritten after a team rename and still need to be PATCHed,
    // keyed by scheduleId then rowId.
    private _dirtyScheduleRows: Map<string, Map<string, ScheduleRowEntity>> = new Map();
    private _pendingCommentAdds: EventCommentEntity[] = [];
    private _pendingCommentDeletes: string[] = [];
    private _pendingCommentUpdates: Map<string, string> = new Map();

    public get currentModel(): Project | null {
        return this._model;
    }

    public exportDraft(): ProjectDraftData | null {
        if (this._model == null) {
            return null;
        }

        return {
            event: this._model.event.toDto(),
            groups: this._model.groups.map(g => g.toDto()),
            venues: this._model.venues.map(v => v.toDto()),
            schedules: this._model.schedules.map(s => ({
                id: s.id,
                eventId: s.eventId,
                groupIds: [...(s.groupIds ?? [])],
                code: s.code,
                startDayOffset: s.startDayOffset,
                sortOrder: s.sortOrder,
                name: s.name,
                concurrency: s.concurrency,
                games: (s.games ?? []).map(g => ({
                    id: g.id,
                    scheduleId: g.scheduleId,
                    homeTeam: g.homeTeam,
                    opponentTeam: g.opponentTeam,
                    fieldId: g.fieldId,
                    interval: g.interval,
                    concurrency: g.concurrency,
                    groupId: g.groupId,
                })),
                fields: (s.fields ?? []).map(f => ({
                    scheduleId: f.scheduleId,
                    fieldNumber: f.fieldNumber,
                    venueId: f.venueId,
                })),
            })),
            groupTeams: Array.from(this._groupTeamsCache.entries()).map(([groupId, teams]) => ({
                groupId,
                teams: teams.map(t => t.toDto()),
            })),
            associationContacts: Array.from(this._associationContactsCache.entries()).map(([key, contacts]) => ({
                key,
                contacts: contacts.map(c => ({ id: c.id, email: c.email })),
            })),
            scheduleCache: Array.from(this._scheduleCache.entries()).map(([scheduleId, cached]) => ({
                scheduleId,
                serie: cached.serie?.toDto() ?? null,
                customIntervals: (cached.customIntervals ?? []).map(ci => ci.toDto()),
                games: (cached.games ?? []).map(g => ({
                    id: g.id,
                    scheduleId: g.scheduleId,
                    homeTeam: g.homeTeam,
                    opponentTeam: g.opponentTeam,
                    fieldId: g.fieldId,
                    interval: g.interval,
                    concurrency: g.concurrency,
                    groupId: g.groupId,
                })),
                fields: (cached.fields ?? []).map(f => ({
                    scheduleId: f.scheduleId,
                    fieldNumber: f.fieldNumber,
                    venueId: f.venueId,
                })),
            })),
            fieldAssignments: Array.from(this._pendingFieldsMap.entries()).map(([scheduleId, fields]) => ({
                scheduleId,
                fields: (fields ?? []).map(f => ({
                    scheduleId: f.scheduleId,
                    fieldNumber: f.fieldNumber,
                    venueId: f.venueId,
                })),
            })),
        };
    }

    public importDraft(draft: ProjectDraftData): string {
        if (!draft?.event?.id) {
            throw new Error('invalid_draft_event');
        }

        const event = EventEntity.fromDto(draft.event);
        const groups = GroupEntity.fromDtoArray(draft.groups ?? []);
        const venues = VenueEntity.fromDtoArray(draft.venues ?? []);
        const schedules = (draft.schedules ?? []).map((s: any) => {
            const schedule = ScheduleEntity.fromDto(s);
            schedule.games = ScheduleRowEntity.fromDtoArray(s.games ?? []);
            schedule.fields = ScheduleFieldEntity.fromDtoArray(s.fields ?? []);
            return schedule;
        });
        const comments: EventCommentEntity[] = [];

        this._model = new Project(event, venues, groups, schedules, comments);
        this.pendingSchedulerModels = [];
        this.pendingGroupDeletes = [];
        this.pendingVenueDeletes = [];
        this.pendingScheduleDeletes = [];
        this._pendingCommentAdds = [];
        this._pendingCommentDeletes = [];
        this._pendingCommentUpdates.clear();
        this._dirtyGroupIds.clear();
        this._dirtyVenueIds.clear();
        this._dirtyVenueScheduleIds.clear();
        this._dirtyTeamGroupIds.clear();
        this._dirtyAssociationContactKeys.clear();
        this._dirtyScheduleIds.clear();
        this._pendingAssociationMemberRemovals.clear();
        this._pendingAssociationDeletes.clear();
        this._pendingAssociationRenames.clear();
        this._dirtyScheduleRows.clear();

        this._groupTeamsCache.clear();
        for (const entry of draft.groupTeams ?? []) {
            this._groupTeamsCache.set(entry.groupId, TeamSettingEntity.fromDtoArray(entry.teams ?? []));
        }

        this._associationContactsCache.clear();
        for (const entry of draft.associationContacts ?? []) {
            this._associationContactsCache.set(entry.key, (entry.contacts ?? []).map(c => ({ id: c.id, email: c.email })));
        }

        this._associationIdCache.clear();
        for (const [groupId, teams] of this._groupTeamsCache) {
            for (const team of teams) {
                this._rememberAssociationId(groupId, team.association, team.associationId);
            }
        }

        this._scheduleCache.clear();
        for (const entry of draft.scheduleCache ?? []) {
            this._scheduleCache.set(entry.scheduleId, {
                serie: entry.serie ? SerieSettingEntity.fromDto(entry.serie) : new SerieSettingEntity(),
                customIntervals: CustomIntervalEntity.fromDtoArray(entry.customIntervals ?? []),
                games: ScheduleRowEntity.fromDtoArray(entry.games ?? []),
                fields: ScheduleFieldEntity.fromDtoArray(entry.fields ?? []),
            });
        }

        this._pendingFieldsMap.clear();
        for (const entry of draft.fieldAssignments ?? []) {
            this._pendingFieldsMap.set(entry.scheduleId, ScheduleFieldEntity.fromDtoArray(entry.fields ?? []));
        }

        // Ensure every schedule has cache data, even if older drafts lacked explicit cache entries.
        for (const schedule of this._model.schedules) {
            if (!this._scheduleCache.has(schedule.id)) {
                this._scheduleCache.set(schedule.id, {
                    serie: new SerieSettingEntity(),
                    customIntervals: [],
                    games: schedule.games ?? [],
                    fields: schedule.fields ?? [],
                });
            }
        }

        const pendingIds = new Set(this._model.schedules.map(s => s.id));
        this.pendingSchedulerModels = this._model.schedules
            .filter(s => pendingIds.has(s.id))
            .map(schedule => {
                const cached = this._scheduleCache.get(schedule.id);
                const teams = this.getTeamsForGroups(schedule.groupIds ?? []);
                const pending = new SchedulerModel(
                    schedule,
                    cached?.serie ?? new SerieSettingEntity(),
                    teams,
                    cached?.customIntervals ?? [],
                    cached?.games ?? []
                );
                if (pending.selectedScheduleRowCollection) {
                    pending.selectedScheduleRowCollection.isSaved = false;
                }

                return pending;
            });

        this._originalGroupIds = new Set();
        this._originalVenueIds = new Set();
        this._originalScheduleIds = new Set();
        this._takeSnapshot();

        return this._model.event.id;
    }

    public takeSnapshot(): void {
        this._takeSnapshot();
    }

    public clear(): void {
        this._model = null;
        this.pendingSchedulerModels = [];
        this.pendingGroupDeletes = [];
        this.pendingVenueDeletes = [];
        this._originalGroupIds = new Set();
        this._originalVenueIds = new Set();
        this._dirtyGroupIds.clear();
        this._dirtyVenueIds.clear();
        this._dirtyVenueScheduleIds.clear();
        this._dirtyTeamGroupIds.clear();
        this._dirtyAssociationContactKeys.clear();
        this._dirtyScheduleIds.clear();
        this._pendingAssociationMemberRemovals.clear();
        this._pendingAssociationDeletes.clear();
        this._pendingAssociationRenames.clear();
        this._dirtyScheduleRows.clear();
        this._pendingCommentAdds = [];
        this._pendingCommentDeletes = [];
        this._pendingCommentUpdates.clear();
        this._groupTeamsCache.clear();
        this._associationContactsCache.clear();
        this._associationIdCache.clear();
        this._scheduleCache.clear();
        this._pendingFieldsMap.clear();
    }

    public get hasExistingSchedules(): boolean {
        return (this._model?.schedules?.length ?? 0) > 0
            || this.pendingSchedulerModels.length > 0;
    }

    // True when user-editable event fields differ from the last load/save snapshot.
    public get isEventDirty(): boolean {
        if (!this._model || this._eventSnapshot === null) { return false; }
        return this._eventFieldsJson() !== this._eventSnapshot;
    }

    // True when any unsaved changes exist (event fields, pending schedules, groups, venues, teams, or comments).
    public get hasUnsavedChanges(): boolean {
        // A brand-new event has never been persisted — always needs saving.
        if (this._model != null && this._model.event.code == null) { return true; }
        return this.isEventDirty
            || this.pendingSchedulerModels.length > 0
            || this.pendingGroupDeletes.length > 0
            || this.pendingVenueDeletes.length > 0
            || this.pendingScheduleDeletes.length > 0
            || this._dirtyGroupIds.size > 0
            || this._dirtyVenueIds.size > 0
            || this._dirtyVenueScheduleIds.size > 0
            || this._dirtyTeamGroupIds.size > 0
            || this._dirtyAssociationContactKeys.size > 0
            || this._pendingAssociationDeletes.size > 0
            || this._pendingAssociationRenames.size > 0
            || this._dirtyScheduleRows.size > 0
            || this._dirtyScheduleIds.size > 0
            || this._pendingCommentAdds.length > 0
            || this._pendingCommentDeletes.length > 0
            || this._pendingCommentUpdates.size > 0
            || this._model?.groups?.some(g => !this._originalGroupIds.has(g.id))
            || this._model?.venues?.some(v => !this._originalVenueIds.has(v.id));
    }
    private _eventApi: EventApi;
    private _groupsApi: GroupsApi;
    private _venuesApi: VenuesApi;
    private _schedulesApi: ScheduleApi;
    private _serieApi: SerieApi;
    private _scheduleRowsApi: ScheduleRowApi;
    private _customIntervalApi: CustomIntervalApi;
    private _scheduleFieldApi: ScheduleFieldApi;
    private _userContext: UserContext;
    private _userApi: UserApi;
    private _teamApi: TeamApi;
    private _groupAssociationMembersApi: GroupAssociationMembersApi;
    private _groupAssociationsApi: GroupAssociationsApi;
    private _languageResolver: LanguageResolver;
    private _groupTeamsCache: Map<string, TeamSettingEntity[]> = new Map();
    // Staged association contacts for a group, keyed by `${groupId}::${associationName}`.
    // Staged client-side and flushed to GroupAssociationMembersApi by saveProject().
    private _associationContactsCache: Map<string, StagedAssociationContact[]> = new Map();
    private _scheduleCache: Map<string, { serie: SerieSettingEntity; customIntervals: CustomIntervalEntity[]; games: ScheduleRowEntity[]; fields: ScheduleFieldEntity[] }> = new Map();
    // Field assignments for pending (unsaved) scheduler models, keyed by scheduleId
    private _pendingFieldsMap: Map<string, ScheduleFieldEntity[]> = new Map();
    // Remembers an association's server id (keyed like _associationContactsCache) even after its
    // last team is removed, so staged contacts can still be located and deleted on the backend.
    private _associationIdCache: Map<string, string> = new Map();

    constructor(eventApi: EventApi, groupsApi: GroupsApi, venuesApi: VenuesApi, schedulesApi: ScheduleApi, serieApi: SerieApi, scheduleRowsApi: ScheduleRowApi, customIntervalApi: CustomIntervalApi, userContext: UserContext, languageResolver: LanguageResolver, userApi: UserApi, teamApi: TeamApi, scheduleFieldApi: ScheduleFieldApi, groupAssociationMembersApi: GroupAssociationMembersApi, groupAssociationsApi: GroupAssociationsApi) {
        this._eventApi = eventApi;
        this._groupsApi = groupsApi;
        this._venuesApi = venuesApi;
        this._schedulesApi = schedulesApi;
        this._serieApi = serieApi;
        this._scheduleRowsApi = scheduleRowsApi;
        this._customIntervalApi = customIntervalApi;
        this._userContext = userContext;
        this._languageResolver = languageResolver;
        this._userApi = userApi;
        this._teamApi = teamApi;
        this._scheduleFieldApi = scheduleFieldApi;
        this._groupAssociationMembersApi = groupAssociationMembersApi;
        this._groupAssociationsApi = groupAssociationsApi;
    }

    public async loadByEventId(eventId: string, forceReload: boolean): Promise<Project> {
        // Never make an API call for an in-memory (unsaved) event
        if (this._model != null && this._model.event.id === eventId && this._model.event.code == null) {
            return this._model;
        }
        if (forceReload || this._model == null || this._model.event.id !== eventId) {
            const event = await this._eventApi.getById(eventId);
            const groups = await this._groupsApi.listByEventId(eventId);
            const venues = await this._venuesApi.listByEventId(eventId);
            const schedules = await this._schedulesApi.listByEventId(eventId);
            const comments = event.code != null ? await this._eventApi.listComments(eventId) : [];
            this._model = new Project(event, venues, groups, schedules, comments);
            // Pre-load teams for all groups into cache
            this._groupTeamsCache.clear();
            await Promise.all(
                this._model.groups.map(async g => {
                    const teams = await this._teamApi.getByGroupId(eventId, g.id);
                    this._groupTeamsCache.set(g.id, teams);
                })
            );
            // Pre-load existing association contacts (from any source: registration or edit-teams)
            // for every association belonging to a group, regardless of whether it still has teams.
            this._associationContactsCache.clear();
            this._associationIdCache.clear();
            await Promise.all(
                this._model.groups.map(async g => {
                    const associations = await this._groupAssociationsApi.listByGroupId(eventId, g.id);
                    await Promise.all(
                        associations.map(async association => {
                            const members = await this._groupAssociationMembersApi.getMembers(eventId, g.id, association.id);
                            const key = this._associationContactsKey(g.id, association.name);
                            this._associationContactsCache.set(key, members.map(m => ({ id: m.id, email: m.email })));
                            this._associationIdCache.set(key, association.id);
                        })
                    );
                })
            );
            // Pre-load schedule detail (serie, custom intervals, games, fields) into cache
            this._scheduleCache.clear();
            this._pendingFieldsMap.clear();
            await Promise.all(
                this._model.schedules.map(async schedule => {
                    const [serie, customIntervals, games, fields] = await Promise.all([
                        this._serieApi.getByScheduleId(schedule.id),
                        this._customIntervalApi.getByScheduleId(schedule.id),
                        this._scheduleRowsApi.listByScheduleId(eventId, schedule.id),
                        this._scheduleFieldApi.listByScheduleId(eventId, schedule.id)
                    ]);
                    this._scheduleCache.set(schedule.id, { serie, customIntervals, games, fields });
                    schedule.games = games;
                    schedule.fields = fields;
                })
            );
            // Capture original IDs and reset pending/dirty state for change tracking
            this._originalGroupIds = new Set(this._model.groups.map(g => g.id));
            this._originalVenueIds = new Set(this._model.venues.map(v => v.id));
            this._originalScheduleIds = new Set(this._model.schedules.map(s => s.id));
            this.pendingGroupDeletes = [];
            this.pendingVenueDeletes = [];
            this.pendingScheduleDeletes = [];
            this._dirtyGroupIds.clear();
            this._dirtyVenueIds.clear();
            this._dirtyVenueScheduleIds.clear();
            this._dirtyTeamGroupIds.clear();
            this._dirtyAssociationContactKeys.clear();
            this._dirtyScheduleIds.clear();
            this._pendingAssociationMemberRemovals.clear();
            this._pendingAssociationDeletes.clear();
            this._pendingAssociationRenames.clear();
            this._dirtyScheduleRows.clear();
            this._pendingCommentAdds = [];
            this._pendingCommentDeletes = [];
            this._pendingCommentUpdates.clear();
            this._takeSnapshot();
        }
        return this._model;
    }

    public getTeamsForGroups(groupIds: string[]): TeamSettingEntity[] {
        const result: TeamSettingEntity[] = [];
        for (const gid of groupIds) {
            const teams = this._groupTeamsCache.get(gid) ?? [];
            result.push(...teams);
        }
        return result;
    }

    public setTeamsForGroup(groupId: string, teams: TeamSettingEntity[]): void {
        this._groupTeamsCache.set(groupId, [...teams]);
    }

    public addComment(text: string): void {
        const entity = new EventCommentEntity();
        entity.id = `temp-${uuidv1()}`; // replaced with server ID on save
        entity.text = text;
        entity.createdAt = new Date().toISOString();
        this._model.comments.unshift(entity);
        this._pendingCommentAdds.push(entity);
    }

    public deleteComment(comment: EventCommentEntity): void {
        const idx = this._model.comments.indexOf(comment);
        if (idx >= 0) { this._model.comments.splice(idx, 1); }
        const addIdx = this._pendingCommentAdds.indexOf(comment);
        if (addIdx >= 0) {
            this._pendingCommentAdds.splice(addIdx, 1);
        } else {
            this._pendingCommentDeletes.push(comment.id);
            this._pendingCommentUpdates.delete(comment.id);
        }
    }

    public updateComment(comment: EventCommentEntity, text: string): void {
        comment.text = text;
        if (!this._pendingCommentAdds.includes(comment)) {
            this._pendingCommentUpdates.set(comment.id, text);
        }
    }

    /** Buffers team changes in memory; flushed to the API by saveProject(). */
    public saveTeamsForGroup(groupId: string, teams: TeamSettingEntity[]): void {
        const existingTeams = this._groupTeamsCache.get(groupId) ?? [];
        const hasChanges = !this._teamSettingsEqual(existingTeams, teams);

        // Remember associationIds before they potentially disappear from the roster below, so
        // an association whose last team is removed can still have its contacts cleaned up.
        for (const team of existingTeams) {
            this._rememberAssociationId(groupId, team.association, team.associationId);
        }

        this._groupTeamsCache.set(groupId, [...teams]);
        // Only ever mark dirty here: "unchanged" is relative to the in-memory cache, so a later
        // no-op visit must not clear a flag set by an earlier edit that is still unsaved.
        if (this._model?.event.code != null && hasChanges) {
            this._dirtyTeamGroupIds.add(groupId);
        }
    }

    private _associationContactsKey(groupId: string, associationName: string): string {
        return `${groupId}::${associationName}`;
    }

    private _rememberAssociationId(groupId: string, associationName: string, associationId: string | null | undefined): void {
        if (!associationId) { return; }
        this._associationIdCache.set(this._associationContactsKey(groupId, associationName), associationId);
    }

    public getAssociationContacts(groupId: string, associationName: string): StagedAssociationContact[] {
        return (this._associationContactsCache.get(this._associationContactsKey(groupId, associationName)) ?? []).map(c => ({ ...c }));
    }

    // Returns every association known for a group (name + server id when known), including ones
    // with no remaining teams (e.g. all teams were removed while contacts from a registration are
    // still staged/persisted).
    public getAssociationsForGroup(groupId: string): { name: string; associationId: string | null }[] {
        const prefix = `${groupId}::`;
        const names = new Set<string>();
        for (const key of this._associationContactsCache.keys()) {
            if (key.startsWith(prefix)) { names.add(key.substring(prefix.length)); }
        }
        for (const key of this._associationIdCache.keys()) {
            if (key.startsWith(prefix)) { names.add(key.substring(prefix.length)); }
        }
        return Array.from(names).map(name => ({
            name,
            associationId: this._associationIdCache.get(this._associationContactsKey(groupId, name)) ?? null,
        }));
    }

    /** Buffers association-contact changes in memory, mirroring saveTeamsForGroup's dirty tracking. */
    public saveAssociationContacts(groupId: string, associationName: string, contacts: StagedAssociationContact[]): void {
        const key = this._associationContactsKey(groupId, associationName);
        const existing = this._associationContactsCache.get(key) ?? [];
        const hasChanges = !this._associationContactsEqual(existing, contacts);

        const removedIds = existing.filter(e => e.id && !contacts.some(c => c.id === e.id)).map(e => e.id);
        if (removedIds.length > 0) {
            this._queueAssociationMemberRemovals(key, removedIds);
        }

        if (contacts.length === 0) {
            this._associationContactsCache.delete(key);
        } else {
            this._associationContactsCache.set(key, contacts.map(c => ({ ...c })));
        }

        if (this._model?.event.code != null && (hasChanges || removedIds.length > 0)) {
            this._dirtyAssociationContactKeys.add(key);
        }
    }

    private _queueAssociationMemberRemovals(key: string, memberIds: string[]): void {
        const queue = this._pendingAssociationMemberRemovals.get(key) ?? new Set<string>();
        for (const id of memberIds) { queue.add(id); }
        this._pendingAssociationMemberRemovals.set(key, queue);
    }

    // Schedule rows reference teams by display name, so a renamed team (association or suffix) must be
    // rewritten in every row of the group's schedules, otherwise those games show up as "removed teams".
    // Every row is looked up once in the original-name map, so swapped names don't get applied twice.
    public renameTeamsInSchedules(groupId: string, renames: { from: string; to: string }[]): void {
        const renamedTo = new Map<string, string>();
        for (const rename of renames) {
            if (rename.from && rename.to && rename.from !== rename.to) { renamedTo.set(rename.from, rename.to); }
        }
        if (renamedTo.size === 0 || this._model == null) { return; }

        const rows = new Map<ScheduleRowEntity, string>();
        const collect = (scheduleId: string, groupIds: string[] | null | undefined, games: ScheduleRowEntity[] | null | undefined): void => {
            if (!groupIds?.includes(groupId)) { return; }
            for (const row of games ?? []) {
                if (row.groupId == null || row.groupId === groupId) { rows.set(row, scheduleId); }
            }
        };
        for (const schedule of this._model.schedules) {
            collect(schedule.id, schedule.groupIds, this._scheduleCache.get(schedule.id)?.games);
            collect(schedule.id, schedule.groupIds, schedule.games);
        }
        for (const pending of this.pendingSchedulerModels) {
            for (const collection of pending.scheduleRowCollections) {
                collect(pending.scheduleId, pending.groupIds, collection.games);
            }
            // The model holds the team entities captured before the rename; keep them in step with the rows.
            if (pending.groupIds?.includes(groupId)) {
                pending.teamSettings = this.getTeamsForGroups(pending.groupIds);
            }
        }

        const isPersisted = this._model.event.code != null;
        for (const [row, scheduleId] of rows) {
            const home = renamedTo.get(row.homeTeam);
            const opponent = renamedTo.get(row.opponentTeam);
            if (home == null && opponent == null) { continue; }
            if (home != null) { row.homeTeam = home; }
            if (opponent != null) { row.opponentTeam = opponent; }
            if (isPersisted && row.id != null) {
                const dirtyRows = this._dirtyScheduleRows.get(scheduleId) ?? new Map<string, ScheduleRowEntity>();
                dirtyRows.set(row.id, row);
                this._dirtyScheduleRows.set(scheduleId, dirtyRows);
            }
        }
    }

    // Moves associations to their new names: staged contacts, queued member removals and the server
    // id follow the association, and a backend rename is queued so the persisted association keeps
    // its id/members instead of being left behind empty while the teams are re-created under the new
    // name. Must run after saveTeamsForGroup, which records the ids of the teams' old associations.
    // Handles the whole batch at once so renames that swap names don't overwrite each other.
    public renameAssociations(groupId: string, renames: { from: string; to: string }[]): void {
        const effective = renames.filter(r => r.from && r.to && r.from !== r.to);
        const detached = effective.map(r => {
            const oldKey = this._associationContactsKey(groupId, r.from);
            const entry = {
                rename: r,
                associationId: this._associationIdCache.get(oldKey) ?? null,
                contacts: this._associationContactsCache.get(oldKey),
                removals: this._pendingAssociationMemberRemovals.get(oldKey),
                wasDirty: this._dirtyAssociationContactKeys.has(oldKey),
            };
            this._associationContactsCache.delete(oldKey);
            this._pendingAssociationMemberRemovals.delete(oldKey);
            this._dirtyAssociationContactKeys.delete(oldKey);
            this._associationIdCache.delete(oldKey);
            return entry;
        });

        for (const entry of detached) {
            const newKey = this._associationContactsKey(groupId, entry.rename.to);
            if (entry.contacts != null) { this._associationContactsCache.set(newKey, entry.contacts); }
            if (entry.removals != null) { this._pendingAssociationMemberRemovals.set(newKey, entry.removals); }
            if (entry.wasDirty || entry.removals != null) { this._dirtyAssociationContactKeys.add(newKey); }
            if (entry.associationId == null) { continue; }

            this._associationIdCache.set(newKey, entry.associationId);
            const from = this._pendingAssociationRenames.get(entry.associationId)?.from ?? entry.rename.from;
            if (from === entry.rename.to) {
                this._pendingAssociationRenames.delete(entry.associationId);
            } else {
                this._pendingAssociationRenames.set(entry.associationId, { groupId, from, to: entry.rename.to });
            }
        }
    }

    // Called when an association's last team is removed — queues its existing (already-persisted)
    // members for deletion too, so orphaned contacts don't linger after the association disappears.
    public removeAssociationContacts(groupId: string, associationName: string): void {
        const key = this._associationContactsKey(groupId, associationName);
        const existing = this._associationContactsCache.get(key) ?? [];
        const existingIds = existing.filter(c => c.id).map(c => c.id);
        if (existingIds.length > 0) {
            this._queueAssociationMemberRemovals(key, existingIds);
        }

        const had = this._associationContactsCache.delete(key);
        if ((had || existingIds.length > 0) && this._model?.event.code != null) {
            this._dirtyAssociationContactKeys.add(key);
        } else if (!this._pendingAssociationMemberRemovals.has(key)) {
            this._dirtyAssociationContactKeys.delete(key);
        }
    }

    // Called when the whole association card is removed (edit-teams trash icon) rather than just
    // emptied of teams. Queues the association itself (contacts + teams cascade server-side) for
    // deletion on the next save, and drops any staged contact state so it isn't re-synced first.
    public deleteAssociation(groupId: string, associationName: string): void {
        const key = this._associationContactsKey(groupId, associationName);
        const associationId = this._groupTeamsCache.get(groupId)?.find(t => t.association === associationName)?.associationId
            ?? this._associationIdCache.get(key);

        this._associationContactsCache.delete(key);
        this._pendingAssociationMemberRemovals.delete(key);
        this._dirtyAssociationContactKeys.delete(key);
        this._associationIdCache.delete(key);

        if (associationId) {
            this._pendingAssociationRenames.delete(associationId);
            this._pendingAssociationDeletes.set(associationId, groupId);
        }
    }

    // Carries staged contacts (and any queued removals) over when a group's temp id is replaced
    // by its server-assigned id.
    private _remapAssociationContactsGroupId(oldGroupId: string, newGroupId: string): void {
        if (oldGroupId === newGroupId) { return; }
        const prefix = `${oldGroupId}::`;
        for (const [key, contacts] of Array.from(this._associationContactsCache.entries())) {
            if (!key.startsWith(prefix)) { continue; }
            const associationName = key.substring(prefix.length);
            const newKey = this._associationContactsKey(newGroupId, associationName);
            this._associationContactsCache.delete(key);
            this._associationContactsCache.set(newKey, contacts);
            if (this._dirtyAssociationContactKeys.delete(key)) {
                this._dirtyAssociationContactKeys.add(newKey);
            }
            const removals = this._pendingAssociationMemberRemovals.get(key);
            if (removals != null) {
                this._pendingAssociationMemberRemovals.delete(key);
                this._pendingAssociationMemberRemovals.set(newKey, removals);
            }
            const associationId = this._associationIdCache.get(key);
            if (associationId != null) {
                this._associationIdCache.delete(key);
                this._associationIdCache.set(newKey, associationId);
            }
        }
    }

    // Applies queued association renames. A rename whose target name is still held by another
    // association awaiting its own rename (chains like A→B, B→C, or swaps) is deferred until that one
    // has moved; a pure cycle is broken by first parking one association under a temporary name.
    private async _flushAssociationRenames(eventId: string): Promise<void> {
        while (this._pendingAssociationRenames.size > 0) {
            const entries = Array.from(this._pendingAssociationRenames.entries());
            const ready = entries.find(([id, r]) => !entries.some(([otherId, o]) => otherId !== id && o.groupId === r.groupId && o.from === r.to));
            if (ready != null) {
                const [id, rename] = ready;
                await this._groupAssociationsApi.renameAssociation(eventId, rename.groupId, id, rename.to);
                this._pendingAssociationRenames.delete(id);
                continue;
            }

            const [id, rename] = entries[0];
            const temporaryName = `~${id}`;
            await this._groupAssociationsApi.renameAssociation(eventId, rename.groupId, id, temporaryName);
            rename.from = temporaryName;
        }
    }

    // Flushes staged contact adds/removes to the backend for every dirty association, resolving
    // each association's real id from its (already-saved) teams in _groupTeamsCache, falling back
    // to _associationIdCache for associations whose last team was removed. Associations that can't
    // yet be resolved (e.g. contacts staged before any team was ever saved) are left dirty for retry.
    private async _flushAssociationContacts(eventId: string): Promise<void> {
        for (const key of Array.from(this._dirtyAssociationContactKeys)) {
            const separatorIdx = key.indexOf('::');
            if (separatorIdx < 0) {
                this._dirtyAssociationContactKeys.delete(key);
                this._pendingAssociationMemberRemovals.delete(key);
                continue;
            }

            const groupId = key.substring(0, separatorIdx);
            const associationName = key.substring(separatorIdx + 2);
            const teams = this._groupTeamsCache.get(groupId) ?? [];
            const associationId = teams.find(t => t.association === associationName)?.associationId
                ?? this._associationIdCache.get(key);
            if (!associationId) { continue; }

            const removals = this._pendingAssociationMemberRemovals.get(key);
            if (removals && removals.size > 0) {
                for (const memberId of removals) {
                    await this._groupAssociationMembersApi.removeMember(eventId, groupId, associationId, memberId);
                }
                this._pendingAssociationMemberRemovals.delete(key);
            }

            const current = this._associationContactsCache.get(key) ?? [];
            const newEmails = current.filter(c => !c.id).map(c => c.email);
            if (newEmails.length > 0) {
                const saved = await this._groupAssociationMembersApi.syncMembers(eventId, groupId, associationId, newEmails);
                this._associationContactsCache.set(key, saved.map(m => ({ id: m.id, email: m.email })));
            }

            this._dirtyAssociationContactKeys.delete(key);
        }
    }

    public getSerieForSchedule(scheduleId: string): SerieSettingEntity {
        return this._scheduleCache.get(scheduleId)?.serie ?? new SerieSettingEntity();
    }

    public getCustomIntervalsForSchedule(scheduleId: string): CustomIntervalEntity[] {
        return this._scheduleCache.get(scheduleId)?.customIntervals ?? [];
    }

    public getGamesForSchedule(scheduleId: string): ScheduleRowEntity[] {
        return this._scheduleCache.get(scheduleId)?.games ?? [];
    }

    public getVenuesForEvent(): VenueEntity[] {
        return this._model?.venues ?? [];
    }

    // ── In-memory mutation methods (no API calls) ─────────────────────────────

    public addGroup(name: string): GroupEntity {
        const group = new GroupEntity();
        group.id = uuidv1();
        group.name = name;
        this._model.groups.add(group);
        return group;
    }

    public updateGroup(groupId: string, name: string): void {
        const group = this._model.groups.find(g => g.id === groupId);
        if (!group) { return; }
        group.name = name;
        if (this._originalGroupIds.has(groupId)) {
            this._dirtyGroupIds.add(groupId);
        }
    }

    public removeGroup(groupId: string): void {
        const group = this._model.groups.find(g => g.id === groupId);
        if (!group) { return; }
        this._model.groups.remove(group);
        if (this._originalGroupIds.has(groupId)) {
            this.pendingGroupDeletes.push(groupId);
        }
        this._dirtyGroupIds.delete(groupId);
    }

    public canRemoveGroup(groupId: string): boolean {
        if (this._model?.schedules.some(s => s.groupIds.includes(groupId))) { return false; }
        if (this.pendingSchedulerModels.some(m => m.groupIds.includes(groupId))) { return false; }
        return true;
    }

    // Buffers the scheduler model into pending. Only adds a new entry when rows
    // were actually generated or the schedule is new — avoids spurious unsaved-changes.
    public finalizeSchedule(model: SchedulerModel, serieSettingsChanged = false): void {
        const project = this._model;
        const entity = model.toEntity();
        entity.games = [...model.games];
        const existingIdx = this.pendingSchedulerModels.findIndex(m => m.scheduleId === model.scheduleId);
        const hasNewRows = model.scheduleRowCollections.some(c => !c.isSaved);
        if (existingIdx >= 0) {
            this.pendingSchedulerModels[existingIdx] = model;
            const schedIdx = project.schedules.findIndex(s => s.id === entity.id);
            if (schedIdx >= 0) { project.schedules[schedIdx] = entity; }
        } else if (model.isNew || hasNewRows || serieSettingsChanged) {
            const schedIdx = project.schedules.findIndex(s => s.id === entity.id);
            if (schedIdx >= 0) { project.schedules[schedIdx] = entity; }
            else { project.schedules.add(entity); }
            this.pendingSchedulerModels.push(model);
        }
        const selected = model.selectedScheduleRowCollection;
        model.scheduleRowCollections.clear();
        if (selected) { model.scheduleRowCollections.add(selected); }
    }

    public removeSchedule(scheduleId: string): void {
        const pendingIdx = this.pendingSchedulerModels.findIndex(m => m.scheduleId === scheduleId);
        if (pendingIdx >= 0) { this.pendingSchedulerModels.splice(pendingIdx, 1); }
        const schedule = this._model.schedules.find(s => s.id === scheduleId);
        if (schedule) { this._model.schedules.remove(schedule); }
        this._scheduleCache.delete(scheduleId);
        this._dirtyScheduleIds.delete(scheduleId);
        if (this._originalScheduleIds.has(scheduleId)) {
            this.pendingScheduleDeletes.push(scheduleId);
        }
    }

    // Appends new schedules after every existing one, same convention as addGroup().
    public getNextScheduleSortOrder(): number {
        const schedules = this._model?.schedules ?? [];
        if (schedules.length === 0) { return 0; }
        return Math.max(...schedules.map(s => s.sortOrder ?? 0)) + 1;
    }

    // Swaps a schedule with its neighbor and renumbers sortOrder to match the new array order.
    // Marks both as dirty so an already-saved schedule that isn't otherwise being edited still
    // gets its new order persisted on the next save.
    public reorderSchedule(scheduleId: string, direction: 'up' | 'down'): void {
        const schedules = this._model?.schedules;
        if (!schedules) { return; }
        const index = schedules.findIndex(s => s.id === scheduleId);
        if (index < 0) { return; }
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= schedules.length) { return; }

        // Use splice (not index assignment) so Aurelia's array observer picks up the change.
        const [moved] = schedules.splice(index, 1);
        schedules.splice(targetIndex, 0, moved);
        schedules[index].sortOrder = index;
        schedules[targetIndex].sortOrder = targetIndex;

        for (const s of [schedules[index], schedules[targetIndex]]) {
            if (this._originalScheduleIds.has(s.id)) {
                this._dirtyScheduleIds.add(s.id);
            }
        }
    }

    public addVenue(name: string): VenueEntity {
        const venue = new VenueEntity();
        venue.id = uuidv1();
        venue.name = name;
        this._model.venues.add(venue);
        return venue;
    }

    public updateVenue(venueId: string, name: string): void {
        const venue = this._model.venues.find(v => v.id === venueId);
        if (!venue) { return; }
        venue.name = name;
        if (this._originalVenueIds.has(venueId)) {
            this._dirtyVenueIds.add(venueId);
        }
    }

    public removeVenue(venueId: string): void {
        const venue = this._model.venues.find(v => v.id === venueId);
        if (!venue) { return; }
        // Clear this venue from all field assignments before removing it
        for (const [scheduleId, cached] of this._scheduleCache) {
            const affected = cached.fields.filter(f => f.venueId === venueId).map(f => f.fieldNumber);
            for (const fieldNumber of affected) {
                this.assignVenueToField(scheduleId, fieldNumber, null);
            }
        }
        for (const [scheduleId, fields] of this._pendingFieldsMap) {
            const affected = fields.filter(f => f.venueId === venueId).map(f => f.fieldNumber);
            for (const fieldNumber of affected) {
                this.assignVenueToField(scheduleId, fieldNumber, null);
            }
        }
        this._model.venues.remove(venue);
        if (this._originalVenueIds.has(venueId)) {
            this.pendingVenueDeletes.push(venueId);
        }
        this._dirtyVenueIds.delete(venueId);
    }

    public assignVenueToField(scheduleId: string, fieldId: number, venueId: string | null): void {
        // Update the fields entry in the schedule cache (saved schedules)
        const cached = this._scheduleCache.get(scheduleId);
        if (cached) {
            const existing = cached.fields.find(f => f.fieldNumber === fieldId);
            if (existing) {
                existing.venueId = venueId;
            } else if (venueId != null) {
                const f = new ScheduleFieldEntity();
                f.scheduleId = scheduleId;
                f.fieldNumber = fieldId;
                f.venueId = venueId;
                cached.fields.push(f);
            }
            // Mirror onto schedule entity so templates stay in sync
            const scheduleEntity = this._model?.schedules.find(s => s.id === scheduleId);
            if (scheduleEntity) { scheduleEntity.fields = cached.fields; }
            this._dirtyVenueScheduleIds.add(scheduleId);
        }
        // Update pending fields map (unsaved scheduler models)
        const pending = this.pendingSchedulerModels.find(m => m.scheduleId === scheduleId);
        if (pending) {
            const pendingFields = this._pendingFieldsMap.get(scheduleId) ?? [];
            const existing = pendingFields.find(f => f.fieldNumber === fieldId);
            if (existing) {
                existing.venueId = venueId;
            } else if (venueId != null) {
                const f = new ScheduleFieldEntity();
                f.scheduleId = scheduleId;
                f.fieldNumber = fieldId;
                f.venueId = venueId;
                pendingFields.push(f);
            }
            this._pendingFieldsMap.set(scheduleId, pendingFields);
            // Mirror onto schedule entity
            const scheduleEntity = this._model?.schedules.find(s => s.id === scheduleId);
            if (scheduleEntity) { scheduleEntity.fields = pendingFields; }
        }
    }

    public getFieldsForSchedule(scheduleId: string): ScheduleFieldEntity[] {
        return this._scheduleCache.get(scheduleId)?.fields
            ?? this._pendingFieldsMap.get(scheduleId)
            ?? [];
    }

    public createNewProject(): Project {
        const newEvent = new EventEntity();
        newEvent.applyLocalhostDefaults();
        newEvent.language = this._languageResolver.currentLanguage;
        newEvent.id = uuidv1();
        this._model = new Project(newEvent, [], [], []);
        this.pendingSchedulerModels = [];
        this.pendingGroupDeletes = [];
        this.pendingVenueDeletes = [];
        this.pendingScheduleDeletes = [];
        this._originalGroupIds = new Set();
        this._originalVenueIds = new Set();
        this._originalScheduleIds = new Set();
        this._dirtyGroupIds.clear();
        this._dirtyVenueIds.clear();
        this._dirtyVenueScheduleIds.clear();
        this._dirtyTeamGroupIds.clear();
        this._dirtyAssociationContactKeys.clear();
        this._groupTeamsCache.clear();
        this._associationContactsCache.clear();
        this._associationIdCache.clear();
        this._scheduleCache.clear();
        this._pendingFieldsMap.clear();
        this._takeSnapshot();
        return this._model;
    }

    /**
     * Persists the current project (user → event → groups → venues → schedules).
     * Refreshes the event snapshot on success so isEventDirty resets to false.
     */
    public async saveProject(): Promise<void> {
        const model = this._model;
        const event = model.event;
        const isNewEvent = event.code == null;
        let user = model.user;

        if (this._userContext.isLoggedIn) {
            // Logged-in: the backend already has the user — just use the id from UserContext.
            if (user.id == null) {
                user.id = this._userContext.userId;
                user.email = this._userContext.userName;
                model.user = user;
            }
        } else if (user.id == null) {
            user.email = this._userContext.userName;
            if (!user.email) { throw new Error('email_required'); }
            user = await this._userApi.add(user);
            model.user = user;
            this._userContext.setUserInfo(user);
        } else {
            user = await this._userApi.update(user.id, user);
        }

        // Create/update event linked to user
        event.userId = user.id;
        if (isNewEvent) {
            const savedEvent = await this._eventApi.add(event);
            model.event.code = savedEvent.code;
            model.event.id = savedEvent.id;
            model.event.concurrency = savedEvent.concurrency;
        } else if (this.isEventDirty) {
            const savedEvent = await this._eventApi.update(event);
            model.event.concurrency = savedEvent.concurrency;
        }

        if (isNewEvent) {
            // Save pending groups — capture originalId before it may be remapped
            const groupIdMap = new Map<string, string>();
            const groupOriginalIds: string[] = model.groups.map(g => g.id);
            for (const group of model.groups) {
                const oldId = group.id;
                const saved = await this._groupsApi.add(model.event.id, group);
                group.id = saved.id;
                group.concurrency = saved.concurrency;
                if (oldId !== saved.id) { groupIdMap.set(oldId, saved.id); }
            }
            // Save any standalone-managed teams from the cache
            for (let i = 0; i < model.groups.length; i++) {
                const cachedTeams = this._groupTeamsCache.get(groupOriginalIds[i]) ?? [];
                const savedTeams: TeamSettingEntity[] = [];
                for (const team of cachedTeams) {
                    team.groupId = model.groups[i].id;
                    savedTeams.push(await this._teamApi.addToGroup(model.event.id, model.groups[i].id, team));
                }
                this._groupTeamsCache.set(groupOriginalIds[i], savedTeams);
            }
            // Remap cache keys from temp UUIDs to server-assigned IDs
            for (let i = 0; i < model.groups.length; i++) {
                const oldId = groupOriginalIds[i];
                const newId = model.groups[i].id;
                if (oldId !== newId) {
                    const cached = this._groupTeamsCache.get(oldId);
                    if (cached != null) {
                        this._groupTeamsCache.delete(oldId);
                        this._groupTeamsCache.set(newId, cached);
                    }
                    this._remapAssociationContactsGroupId(oldId, newId);
                }
            }
            await this._flushAssociationContacts(model.event.id);
            // Patch any schedule groupIds that referenced the old temp UUIDs
            if (groupIdMap.size > 0) {
                for (const pending of this.pendingSchedulerModels) {
                    const entity = pending.toEntity();
                    entity.groupIds = entity.groupIds.map(gid => groupIdMap.get(gid) ?? gid);
                    // Also patch per-row groupId so the FK_ScheduleRow_Group constraint is satisfied.
                    for (const row of pending.selectedScheduleRowCollection.games) {
                        if (row.groupId != null && groupIdMap.has(row.groupId)) {
                            row.groupId = groupIdMap.get(row.groupId);
                        }
                    }
                }
            }
            // Save pending venues — capture oldId→savedId mapping
            const venueIdMap = new Map<string, string>();
            for (const venue of model.venues) {
                const oldId = venue.id;
                const saved = await this._venuesApi.add(model.event.id, venue);
                venue.id = saved.id;
                venue.concurrency = saved.concurrency;
                if (oldId !== saved.id) { venueIdMap.set(oldId, saved.id); }
            }
            // Patch any field venueIds that referenced the old temp UUIDs
            if (venueIdMap.size > 0) {
                for (const [, fields] of this._pendingFieldsMap) {
                    for (const f of fields) {
                        if (f.venueId != null && venueIdMap.has(f.venueId)) {
                            f.venueId = venueIdMap.get(f.venueId);
                        }
                    }
                }
                for (const [, cached] of this._scheduleCache) {
                    for (const f of cached.fields) {
                        if (f.venueId != null && venueIdMap.has(f.venueId)) {
                            f.venueId = venueIdMap.get(f.venueId);
                        }
                    }
                }
            }
            // Save all pending scheduler models
            for (const pending of this.pendingSchedulerModels) {
                pending.toEntity().eventId = model.event.id;
                const savedModel = await this.save(pending);
                this._syncSavedSchedule(model, pending.scheduleId, savedModel);
            }
            this.pendingSchedulerModels = [];
        } else {
            // ── Existing event — flush all pending mutations ──────────────────

            // Delete removed schedules (child entities are cascade-deleted by the backend endpoint)
            for (const scheduleId of this.pendingScheduleDeletes) {
                await this._schedulesApi.deleteById(event.id, scheduleId);
            }
            this.pendingScheduleDeletes = [];

            // Delete removed groups
            for (const groupId of this.pendingGroupDeletes) {
                await this._groupsApi.deleteById(event.id, groupId);
            }
            this.pendingGroupDeletes = [];

            // Add new groups (temp UUID → server ID) and update renamed existing groups
            const groupIdMap = new Map<string, string>();
            for (const group of model.groups) {
                if (!this._originalGroupIds.has(group.id)) {
                    const oldId = group.id;
                    const saved = await this._groupsApi.add(event.id, group);
                    group.id = saved.id;
                    group.eventId = saved.eventId;
                    group.concurrency = saved.concurrency;
                    if (oldId !== saved.id) { groupIdMap.set(oldId, saved.id); }
                    const teams = this._groupTeamsCache.get(oldId);
                    if (teams) {
                        this._groupTeamsCache.delete(oldId);
                        const savedTeams: TeamSettingEntity[] = [];
                        for (const team of teams) {
                            team.groupId = saved.id;
                            savedTeams.push(await this._teamApi.addToGroup(event.id, saved.id, team));
                        }
                        this._groupTeamsCache.set(saved.id, savedTeams);
                        this._remapAssociationContactsGroupId(oldId, saved.id);
                    }
                } else if (this._dirtyGroupIds.has(group.id)) {
                    const saved = await this._groupsApi.update(event.id, group.id, group);
                    group.concurrency = saved.concurrency;
                }
            }
            this._dirtyGroupIds.clear();

            // Remap temp groupIds in pending schedule rows
            if (groupIdMap.size > 0) {
                for (const pending of this.pendingSchedulerModels) {
                    pending.groupIds = pending.groupIds.map(gid => groupIdMap.get(gid) ?? gid);
                    for (const row of pending.selectedScheduleRowCollection?.games ?? []) {
                        if (row.groupId != null && groupIdMap.has(row.groupId)) {
                            row.groupId = groupIdMap.get(row.groupId);
                        }
                    }
                }
            }

            // Delete removed venues
            for (const venueId of this.pendingVenueDeletes) {
                await this._venuesApi.deleteById(event.id, venueId);
            }
            this.pendingVenueDeletes = [];

            // Add new venues (temp UUID → server ID) and update renamed existing venues
            const venueIdMap = new Map<string, string>();
            for (const venue of model.venues) {
                if (!this._originalVenueIds.has(venue.id)) {
                    const oldId = venue.id;
                    const saved = await this._venuesApi.add(event.id, venue);
                    venue.id = saved.id;
                    venue.eventId = saved.eventId;
                    venue.concurrency = saved.concurrency;
                    if (oldId !== saved.id) { venueIdMap.set(oldId, saved.id); }
                } else if (this._dirtyVenueIds.has(venue.id)) {
                    const saved = await this._venuesApi.update(event.id, venue.id, venue);
                    venue.concurrency = saved.concurrency;
                }
            }
            this._dirtyVenueIds.clear();

            // Remap temp venueIds in pending field maps and cache
            if (venueIdMap.size > 0) {
                for (const [, fields] of this._pendingFieldsMap) {
                    for (const f of fields) {
                        if (f.venueId != null && venueIdMap.has(f.venueId)) {
                            f.venueId = venueIdMap.get(f.venueId);
                        }
                    }
                }
                for (const [, cached] of this._scheduleCache) {
                    for (const f of cached.fields) {
                        if (f.venueId != null && venueIdMap.has(f.venueId)) {
                            f.venueId = venueIdMap.get(f.venueId);
                        }
                    }
                }
            }

            // Save field-venue assignments for existing schedules with dirty mappings
            for (const scheduleId of this._dirtyVenueScheduleIds) {
                if (this.pendingSchedulerModels.some(m => m.scheduleId === scheduleId)) { continue; }
                const cached = this._scheduleCache.get(scheduleId);
                if (cached) {
                    for (const f of cached.fields) {
                        await this._scheduleFieldApi.put(event.id, scheduleId, f.fieldNumber, f.venueId);
                    }
                }
            }
            this._dirtyVenueScheduleIds.clear();

            // Permanently remove associations deleted via the edit-teams trash icon (teams and
            // contacts cascade server-side). Done before renames and the team re-creation so a
            // same-named association can be renamed to / re-created without hitting the old one.
            for (const [associationId, groupId] of this._pendingAssociationDeletes) {
                await this._groupAssociationsApi.deleteAssociation(event.id, groupId, associationId);
            }
            this._pendingAssociationDeletes.clear();

            // Rename before the teams are re-created below, so they resolve to the renamed
            // association instead of creating a new one next to the old (now empty) one.
            await this._flushAssociationRenames(event.id);

            // Save teams for existing groups whose rosters were changed
            for (const groupId of this._dirtyTeamGroupIds) {
                if (!model.groups.some(g => g.id === groupId)) { continue; }
                const teams = this._groupTeamsCache.get(groupId) ?? [];
                await this._teamApi.deleteAllByGroupId(event.id, groupId);
                const savedTeams: TeamSettingEntity[] = [];
                for (const team of teams) {
                    savedTeams.push(await this._teamApi.addToGroup(event.id, groupId, team));
                }
                this._groupTeamsCache.set(groupId, savedTeams);
            }
            this._dirtyTeamGroupIds.clear();

            await this._flushAssociationContacts(event.id);

            // Persist team renames carried into the rows of existing schedules. Schedules with a pending
            // model are skipped because saving the model re-creates all of its rows.
            for (const [scheduleId, dirtyRows] of this._dirtyScheduleRows) {
                if (this.pendingSchedulerModels.some(m => m.scheduleId === scheduleId)) { continue; }
                for (const [rowId, row] of dirtyRows) {
                    const saved = await this._scheduleRowsApi.update(event.id, scheduleId, rowId, row);
                    row.concurrency = saved.concurrency;
                }
            }
            this._dirtyScheduleRows.clear();

            // Persist reordered schedules that aren't otherwise being edited via a pending model
            for (const scheduleId of this._dirtyScheduleIds) {
                if (this.pendingSchedulerModels.some(m => m.scheduleId === scheduleId)) { continue; }
                const schedule = model.schedules.find(s => s.id === scheduleId);
                if (schedule) {
                    const saved = await this._schedulesApi.update(event.id, schedule.id, schedule);
                    schedule.concurrency = saved.concurrency;
                }
            }
            this._dirtyScheduleIds.clear();

            // Save all pending scheduler models
            for (const pending of this.pendingSchedulerModels) {
                pending.toEntity().eventId = event.id;
                const savedModel = await this.save(pending);
                // Keep the cached model's serie in sync so stale re-edits get the correct id.
                pending.serie = savedModel.serie;
                // Mark the selected collection as saved so re-edits don't re-trigger pending.
                const savedCollection = pending.selectedScheduleRowCollection;
                if (savedCollection) { savedCollection.isSaved = true; }
                this._syncSavedSchedule(model, pending.scheduleId, savedModel);
            }
            this.pendingSchedulerModels = [];
        }

        // Update tracked original IDs to reflect the post-save state
        this._originalGroupIds = new Set(model.groups.map(g => g.id));
        this._originalVenueIds = new Set(model.venues.map(v => v.id));
        this._originalScheduleIds = new Set(model.schedules.map(s => s.id));

        // Flush pending comment operations
        for (const commentId of this._pendingCommentDeletes) {
            await this._eventApi.deleteComment(model.event.id, commentId);
        }
        this._pendingCommentDeletes = [];
        for (const [commentId, text] of this._pendingCommentUpdates) {
            await this._eventApi.updateComment(model.event.id, commentId, text);
        }
        this._pendingCommentUpdates.clear();
        for (const comment of this._pendingCommentAdds) {
            const created = await this._eventApi.addComment(model.event.id, comment.text);
            comment.id = created.id;
            comment.createdAt = created.createdAt;
        }
        this._pendingCommentAdds = [];

        this._takeSnapshot();
    }
    public async save(model: SchedulerModel): Promise<SchedulerModel> {
        if (model.isNew) {
            const scheduleEntity = model.toEntity();
            const savedSchedule = await this._schedulesApi.add(scheduleEntity.eventId, scheduleEntity);
            const serie = await this._serieApi.add(savedSchedule.id, model.serie);
            const teamSettings = await this._addTeams(model, savedSchedule.eventId, savedSchedule.groupIds);
            const customIntervals = await this._addCustomIntervals(model, false, savedSchedule.id);
            const games = await this._addScheduleRows(model, false, savedSchedule.eventId, savedSchedule.id);
            // Save pending field-venue assignments for the new schedule
            const pendingFields = this._pendingFieldsMap.get(model.scheduleId) ?? [];
            for (const f of pendingFields) {
                await this._scheduleFieldApi.put(savedSchedule.eventId, savedSchedule.id, f.fieldNumber, f.venueId);
            }
            return new SchedulerModel(savedSchedule, serie, teamSettings, customIntervals, games);
        } else {
            const scheduleEntity = model.toEntity();
            const savedSchedule = await this._schedulesApi.update(scheduleEntity.eventId, scheduleEntity.id, scheduleEntity);
            const serie = await this._serieApi.update(scheduleEntity.id, model.serie.id, model.serie);
            const teamSettings = await this._addTeams(model, model.eventId, model.groupIds);
            const customIntervals = await this._addCustomIntervals(model, true, model.scheduleId);
            const games = await this._addScheduleRows(model, true, model.eventId, model.scheduleId);
            model.selectedScheduleRowCollection.updateGames(games);
            return new SchedulerModel(savedSchedule, serie, teamSettings, customIntervals, games);
        }
    }

    private _teamSettingsEqual(left: TeamSettingEntity[] | undefined, right: TeamSettingEntity[] | undefined): boolean {
        const leftTeams = left ?? [];
        const rightTeams = right ?? [];
        if (leftTeams.length !== rightTeams.length) {
            return false;
        }

        for (let i = 0; i < leftTeams.length; i++) {
            const leftDto = leftTeams[i]?.toDto?.() ?? leftTeams[i];
            const rightDto = rightTeams[i]?.toDto?.() ?? rightTeams[i];
            if (JSON.stringify(leftDto) !== JSON.stringify(rightDto)) {
                return false;
            }
        }

        return true;
    }

    private _associationContactsEqual(left: StagedAssociationContact[], right: StagedAssociationContact[]): boolean {
        if (left.length !== right.length) {
            return false;
        }

        for (let i = 0; i < left.length; i++) {
            if ((left[i].id ?? '') !== (right[i].id ?? '') || left[i].email !== right[i].email) {
                return false;
            }
        }

        return true;
    }

    private async _addTeams(model: SchedulerModel, eventId: string, groupIds: string[]): Promise<TeamSettingEntity[]> {
        if (groupIds == null || groupIds.length === 0) { return model.teamSettings; }
        const groupId = groupIds[0];
        const promises = new Array<Promise<TeamSettingEntity>>();
        for (const teamSetting of model.teamSettings) {
            if (teamSetting.id == null) {
                promises.add(this._teamApi.addToGroup(eventId, groupId, teamSetting));
            }
        }
        const saved = await Promise.all(promises);
        let saveIdx = 0;
        return model.teamSettings.map(t => t.id != null ? t : saved[saveIdx++]);
    }

    private async _addCustomIntervals(model: SchedulerModel, isUpdate: boolean, scheduleId: string): Promise<CustomIntervalEntity[]> {
        if (isUpdate) { await this._customIntervalApi.deleteByScheduleId(scheduleId); }
        const promises = model.customIntervals.map(ci => this._customIntervalApi.add(scheduleId, ci));
        return Promise.all(promises);
    }

    private async _addScheduleRows(model: SchedulerModel, isUpdate: boolean, eventId: string, scheduleId: string): Promise<ScheduleRowEntity[]> {
        if (isUpdate) { await this._scheduleRowsApi.deleteByScheduleId(eventId, scheduleId); }
        const result: ScheduleRowEntity[] = [];
        const batch = new Array<Promise<ScheduleRowEntity>>();
        let i = 0;
        for (const row of model.selectedScheduleRowCollection.games) {
            batch.add(this._scheduleRowsApi.add(eventId, scheduleId, row));
            if (++i % 10 === 0) {
                result.addRange(await Promise.all(batch));
                batch.clear();
            }
        }
        if (batch.length > 0) { result.addRange(await Promise.all(batch)); }
        return result;
    }
    private _syncSavedSchedule(model: Project, oldScheduleId: string, savedModel: SchedulerModel): void {
        const savedEntity = savedModel.toEntity();
        const schedIdx = model.schedules.findIndex(s => s.id === oldScheduleId);
        if (schedIdx >= 0) {
            const s = model.schedules[schedIdx];
            s.id = savedEntity.id;
            s.code = savedEntity.code;
            s.concurrency = savedEntity.concurrency;
            s.eventId = savedEntity.eventId;
        }
        // Migrate pending fields from old temp ID to server ID; fall back to
        // existing cached fields so existing schedule re-edits don't lose mappings.
        const existingFields = this._scheduleCache.get(oldScheduleId)?.fields;
        const pendingFields = this._pendingFieldsMap.get(oldScheduleId) ?? existingFields ?? [];
        for (const f of pendingFields) { f.scheduleId = savedEntity.id; }
        this._scheduleCache.set(savedEntity.id, {
            serie: savedModel.serie,
            customIntervals: savedModel.customIntervals,
            games: savedModel.games,
            fields: pendingFields
        });
        if (savedEntity.id !== oldScheduleId) {
            this._scheduleCache.delete(oldScheduleId);
            this._pendingFieldsMap.delete(oldScheduleId);
        }
    }

    // ── Snapshot helpers ─────────────────────────────────────────────────────

    private _eventFieldsJson(): string | null {
        const e = this._model?.event;
        if (!e) { return null; }
        return JSON.stringify({
            title: e.title ?? null,
            organizer: e.organizer ?? null,
            location: e.location ?? null,
            latitude: e.latitude ?? null,
            longitude: e.longitude ?? null,
            description: e.description ?? null,
            language: e.language ?? null,
            startDate: e.startDate?.toString() ?? null,
            endDate: e.endDate?.toString() ?? null,
            registrationEnabled: e.registrationEnabled ?? false,
            registrationExpirationDate: e.registrationExpirationDate?.toString() ?? null,
            visibility: e.visibility,
            sport: e.sport,
            sportOther: e.sportOther ?? null,
        });
    }

    private _takeSnapshot(): void {
        this._eventSnapshot = this._eventFieldsJson();
    }
}
